import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-brand text-brand-foreground hover:bg-brand-600",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        // Semantic tones — mirror the legacy Chip's tone palette so call sites
        // can switch with a single variant prop instead of bespoke className.
        success: "border-[color:var(--good-500)]/30 bg-[color:var(--good-50)] text-[color:var(--good-600)]",
        warning: "border-[color:var(--warn-500)]/30 bg-[color:var(--warn-50)] text-[color:var(--warn-600)]",
        // Soft-tint danger — same tone family as `warning`, unlike the solid-fill
        // `destructive` variant. Use this (not `destructive`) wherever it sits
        // next to a `warning` badge, so the pair reads as one tone language.
        critical: "border-[color:var(--bad-500)]/30 bg-[color:var(--bad-50)] text-[color:var(--bad-600)]",
        info: "border-[color:var(--info-500)]/30 bg-[color:var(--info-50)] text-[color:var(--info-500)]",
        neutral: "border-[color:var(--ink-200)] bg-[color:var(--ink-50)] text-[color:var(--ink-700)]",
        brand: "border-[color:var(--brand-500)]/30 bg-[color:var(--brand-50)] text-[color:var(--brand-600)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
